import { useState } from 'react'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Plus,
  Sparkles,
} from 'lucide-react'
import type { AppData } from './api'
import type { Transaction } from './financial-engine'
import { monthFlow, monthKey, balanceTone, type FlowDay } from './cashflow'
import { todayKey } from './income-plan'
import { Button } from './components/ui/button'
import { Card } from './components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './components/ui/dialog'
import { Badge } from './components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './components/ui/popover'
import { toast } from 'sonner'
const money = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})
function FlowValue({
  value,
  className,
  items,
  empty = '—',
}: {
  value: number
  className?: string
  items: FlowDay['items']
  empty?: string
}) {
  const [open, setOpen] = useState(false)
  if (!value && !items.length) return <span>{empty}</span>
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={`flow-hover-value ${className || ''}`}
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') setOpen(true)
          }}
          aria-label={`Detalhes de ${money.format(value)}`}
        >
          {value ? money.format(value) : empty}
          <i className="note-dot" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="flow-hover-card"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <strong>Composição do valor</strong>
        {items.map((item) => (
          <div className="flow-note-item" key={item.id}>
            <div>
              <span>{item.description}</span>
              <b>{money.format(item.amount)}</b>
            </div>
            {item.notes && <p>{item.notes}</p>}
            {item.details?.map((detail, index) => (
              <div className="flow-note-detail" key={index}>
                <span>{detail.description}</span>
                <span>{money.format(detail.amount)}</span>
              </div>
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  )
}
export function MonthlyFlow({
  data,
  edit,
  add,
  update,
  remove,
}: {
  data: AppData
  remove: (t: Transaction) => void
  edit: (t: Transaction) => void
  add: (date: string, kind?: 'income' | 'expense') => void
  update: (
    id: string,
    status: 'planned' | 'completed' | 'cancelled',
  ) => Promise<void>
}) {
  const [offset, setOffset] = useState(()=>{const date=new URLSearchParams(location.search).get('date');if(!date||!/^\d{4}-\d{2}-\d{2}$/.test(date))return 0;const current=monthKey().split('-').map(Number);return Math.max(-24,Math.min(24,(Number(date.slice(0,4))-current[0])*12+Number(date.slice(5,7))-current[1]))}),
    [selectedDate, setSelectedDate] = useState<string | null>(()=>{const date=new URLSearchParams(location.search).get('date');return date&&/^\d{4}-\d{2}-\d{2}$/.test(date)?date:null}),
    [busy, setBusy] = useState(false)
  const month = monthKey(offset),
    rows = monthFlow(data, month),
    today = todayKey(),
    label = new Intl.DateTimeFormat('pt-BR', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(month + '-01T12:00:00'))
  const selected = rows.find((row) => row.date === selectedDate)
  const total = (key: 'income' | 'fixed' | 'daily' | 'simulation') =>
    rows.reduce((s, r) => s + r[key], 0)
  return (
    <section className="module-page monthly-flow">
      <div className="module-title">
        <div>
          <span className="eyebrow">FLUXO MENSAL</span>
          <h2>Seu mês, em uma só visão</h2>
          <p>Entradas, contas fixas, gastos diários e o saldo de cada dia.</p>
        </div>
        <div className="flow-month-picker">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Mês anterior"
            disabled={offset <= -24}
            onClick={() => setOffset((v) => v - 1)}
          >
            <ChevronLeft />
          </Button>
          <strong>{label}</strong>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Próximo mês"
            disabled={offset >= 24}
            onClick={() => setOffset((v) => v + 1)}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
      <div className="flow-summary">
        <Card>
          <span>Entradas registradas / previstas</span>
          <strong className="positive">{money.format(total('income'))}</strong>
        </Card>
        <Card>
          <span>Saídas fixas + diário</span>
          <strong>{money.format(total('fixed') + total('daily'))}</strong>
        </Card>
        <Card className="glass-highlight">
          <span>Saldo projetado no fim do mês</span>
          <strong>{money.format(rows.at(-1)?.balance || 0)}</strong>
        </Card>
      </div>
      <Card className="flow-sheet">
        <div className="flow-sheet-caption">
          <span>
            <Sparkles /> {money.format(total('simulation'))} em receita simulada
          </span>
          <small>
            Passe o mouse ou toque nos valores. Toque na data para abrir o dia.
          </small>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Entrada</TableHead>
              <TableHead>Saída fixa</TableHead>
              <TableHead>Diário</TableHead>
              <TableHead>Saldo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const incomeItems = row.items.filter((i) => !i.transferId && i.kind === 'income'),
                fixedItems = row.items.filter(
                  (i) =>
                    !i.transferId && i.kind === 'expense' &&
                    i.expenseClass !== 'daily' &&
                    i.paymentMethod !== 'credit_card',
                ),
                dailyItems = row.items.filter(
                  (i) =>
                    !i.transferId && i.kind === 'expense' &&
                    i.expenseClass === 'daily' &&
                    i.paymentMethod !== 'credit_card',
                )
              return (
                <TableRow
                  key={row.date}
                  className={row.date === today ? 'flow-today' : ''}
                >
                  <TableCell>
                    <Button
                      variant="ghost"
                      className="flow-date"
                      onClick={() => setSelectedDate(row.date)}
                      aria-label={`Ver lançamentos de ${row.date}`}
                    >
                      <b>{row.date.slice(8)}</b>
                      <small>
                        {new Intl.DateTimeFormat('pt-BR', {
                          weekday: 'short',
                        }).format(new Date(row.date + 'T12:00:00'))}
                      </small>
                      {row.date === today && <i />}
                    </Button>
                  </TableCell>
                  <TableCell className="flow-income">
                    {row.income ? (
                      <FlowValue value={row.income} items={incomeItems} />
                    ) : row.reminder ? (
                      <Button
                        variant="ghost"
                        className="income-reminder"
                        onClick={() => add(row.date, 'income')}
                        aria-label={`Registrar recebimento de ${row.date}`}
                      >
                        R$ 0,00 <Plus size={12} />
                      </Button>
                    ) : (
                      '—'
                    )}
                    {row.simulation > 0 && (
                      <small className="simulated-value">
                        + {money.format(row.simulation)} simulado
                      </small>
                    )}
                  </TableCell>
                  <TableCell>
                    <FlowValue
                      value={row.fixed - row.savings}
                      items={fixedItems.filter(
                        (i) => i.movementPurpose !== 'savings',
                      )}
                    />
                    {row.savings > 0 && (
                      <FlowValue
                        value={row.savings}
                        className="flow-savings"
                        items={fixedItems.filter(
                          (i) => i.movementPurpose === 'savings',
                        )}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    className={row.date >= today ? 'daily-estimate' : ''}
                  >
                    <FlowValue
                      value={row.daily}
                      items={dailyItems}
                      empty={money.format(0)}
                    />
                  </TableCell>
                  <TableCell
                    className={`balance-cell ${balanceTone(row.balance)}`}
                  >
                    {money.format(row.balance)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <div className="flow-legend">
          <span>
            <i className="savings" /> economia
          </span>
          <span>
            <i className="critical" /> abaixo de −R$ 1 mil
          </span>
          <span>
            <i className="negative" /> negativo
          </span>
          <span>
            <i className="low" /> até R$ 1 mil
          </span>
          <span>
            <i className="medium" /> até R$ 2,5 mil
          </span>
          <span>
            <i className="healthy" /> acima de R$ 2,5 mil
          </span>
        </div>
        <p className="flow-footnote">
          Diário futuro: maior valor entre gastos registrados e reserva diária
          do orçamento. Compras no crédito entram na fatura, não no saldo do
          dia. Receitas simuladas são estimativas.
        </p>
      </Card>
      <Dialog
        open={!!selected}
        onOpenChange={(v) => !v && setSelectedDate(null)}
      >
        <DialogContent className="module-dialog day-dialog">
          <DialogHeader>
            <span className="eyebrow">SEU DIA EM DETALHES</span>
            <DialogTitle>
              {selected &&
                new Intl.DateTimeFormat('pt-BR', {
                  day: 'numeric',
                  month: 'long',
                  weekday: 'long',
                }).format(new Date(selected.date + 'T12:00:00'))}
            </DialogTitle>
            <DialogDescription>
              Movimentações e previsões que compõem o saldo deste dia.
            </DialogDescription>
          </DialogHeader>
          <div className="day-summary">
            <div>
              <span>Entradas</span>
              <strong className="positive">
                {money.format(selected?.income || 0)}
              </strong>
            </div>
            <div>
              <span>Saídas + diário</span>
              <strong>
                {money.format((selected?.fixed || 0) + (selected?.daily || 0))}
              </strong>
            </div>
            <div>
              <span>Saldo projetado</span>
              <strong>{money.format(selected?.balance || 0)}</strong>
            </div>
          </div>
          {!!selected?.simulation && (
            <p className="form-notice">
              Receita simulada: {money.format(selected.simulation)}. Ainda não
              foi recebida.
            </p>
          )}
          <div className="day-items">
            {selected?.items.length ? (
              selected.items.filter(item=>!item.transferId||item.kind==='expense'||!selected.items.some(other=>other.transferId===item.transferId&&other.kind==='expense')).map((item) => {
                const tx = data.transactions.find((t) => t.id === item.id)
                return (
                  <Card
                    key={item.id}
                    className={`day-item ${item.movementPurpose === 'savings' ? 'saving' : ''}`}
                  >
                    <div className="day-item-head">
                      <div>
                        <strong>{item.description}</strong>
                        <small>
                          {tx
                            ? `${tx.account} · ${tx.transferId?'Transferência entre contas':tx.category}`
                            : 'Previsão automática'}
                          {item.paymentMethod === 'credit_card'
                            ? ' · cartão de crédito'
                            : ''}
                        </small>
                      </div>
                      <strong
                        className={!item.transferId&&item.kind === 'income' ? 'positive' : ''}
                      >
                        {item.transferId?'↔':item.kind === 'income' ? '+' : '−'}{' '}
                        {money.format(item.amount)}
                      </strong>
                    </div>
                    {item.notes && (
                      <p className="transaction-note">{item.notes}</p>
                    )}
                    {item.details?.map((detail, i) => (
                      <div className="flow-note-detail" key={i}>
                        <span>{detail.description}</span>
                        <span>{money.format(detail.amount)}</span>
                      </div>
                    ))}
                    <div className="day-item-actions">
                      <Badge variant="secondary">
                        {item.status === 'completed'
                          ? 'Realizado'
                          : item.projection
                            ? 'Projeção'
                            : 'Previsto'}
                      </Badge>
                      {tx && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedDate(null)
                              edit(tx)
                            }}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedDate(null)
                              remove(tx)
                            }}
                          >
                            Excluir
                          </Button>
                        </>
                      )}
                      {item.status === 'planned' &&
                        (tx || item.movementPurpose === 'card_payment') && (
                          <Button
                            disabled={busy}
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              setBusy(true)
                              try {
                                await update(item.id, 'completed')
                                if (!tx) setSelectedDate(null)
                              } catch (e) {
                                toast.error(
                                  e instanceof Error
                                    ? e.message
                                    : 'Não foi possível atualizar',
                                )
                              } finally {
                                setBusy(false)
                              }
                            }}
                          >
                            {tx ? 'Marcar realizado' : 'Ver fatura'}
                          </Button>
                        )}
                    </div>
                  </Card>
                )
              })
            ) : (
              <p className="no-results">Nenhuma movimentação neste dia.</p>
            )}
          </div>
          <Button
            variant="liquid"
            className="primary-button"
            onClick={() => {
              if (selected) {
                add(selected.date)
                setSelectedDate(null)
              }
            }}
          >
            <Plus /> Adicionar movimentação
          </Button>
        </DialogContent>
      </Dialog>
    </section>
  )
}
